import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import Meeting from '@/App/Booking/Meeting/Entity/Meeting'
import Service from '@/App/Service/Entity/Service'
import User from '@/App/User/Entity/User'
import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
import ReservationData from '@/App/Booking/Reservation/Entity/ReservationData'
import AttendanceData from '@/App/Booking/Attendance/Entity/AttendanceData'
import ReservationID from '@/App/Booking/Reservation/Entity/ReservationID'
import type { MeetingDataRawData } from '@/Shared/AutoGenerated/Main'

export default class MeetingData {
    constructor(
        public readonly meeting: Meeting,
        public readonly attendancesData: AttendanceData[],
        public readonly reservationsData: ReservationData[],
        public readonly service: Service,
        public readonly companyUser: CompanyUser,
        public readonly user: User,
    ) {
        ensureNoUndefinedProperties(this)
    }

    isSame(other: MeetingData): boolean {
        return this.meeting.id.value === other.meeting.id.value
    }

    hasSingleReservation(): boolean {
        return this.reservationsData.length === 1
    }

    hasSingleReservationWithNotes(): boolean {
        return (
            this.hasSingleReservation() &&
            this.getSingleExistingReservationData().reservation.notes !== null
        )
    }

    getSingleExistingAttendanceData(): AttendanceData {
        if (this.attendancesData.length !== 1) {
            throw new Error('There should be a single attendance')
        }

        return this.attendancesData[0]
    }

    getSingleExistingReservationData(): ReservationData {
        if (this.reservationsData.length !== 1) {
            throw new Error('There should be a single reservation')
        }

        return this.reservationsData[0]
    }

    getAttendanceDataByReservation(
        reservationId: ReservationID,
    ): AttendanceData {
        const items = this.attendancesData.filter(
            (attendanceData: AttendanceData) => {
                return (
                    attendanceData.attendance.reservationId.value ===
                    reservationId.value
                )
            },
        )

        if (items.length === 0) {
            throw new Error(
                `Attendance for customer '${reservationId.value}' should not be null`,
            )
        }

        if (items.length > 1) {
            throw new Error(
                `Attendance for customer '${reservationId.value}' should not have multiple attendances`,
            )
        }

        return items[0]
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingleOrNull(
        entityData: MeetingDataRawData | null,
    ): MeetingData | null {
        if (!entityData) {
            return null
        }

        return this.hydrateSingle(entityData)
    }

    static hydrateSingle(entityData: MeetingDataRawData): MeetingData {
        return new MeetingData(
            Meeting.hydrateSingle(entityData.meetingRawData),
            AttendanceData.hydrateMultiple(entityData.attendancesDataRawData),
            ReservationData.hydrateMultiple(entityData.reservationsDataRawData),
            Service.hydrateSingle(entityData.serviceRawData),
            CompanyUser.hydrateSingle(entityData.companyUserRawData),
            User.hydrateSingle(entityData.userRawData),
        )
    }

    static hydrateMultiple(entitiesData: MeetingDataRawData[]): MeetingData[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
